import React, { useCallback, useMemo, useRef, useState } from "react";
import { ReportGrid } from "./serverGridParent";
import { useMutation } from "react-query";
import { extractHiddenColumns, formatFilterBy, formatSortBy } from "../utils";
import { useTranslation } from "react-i18next";

type TReportWrapper = {
  reportID: string;
  reportName: string;
  metaData: any;
  getAPIFn: any;
  otherAPIRequestPara: any;
  maxHeight: number;
  onClickActionEvent: Function;
  defaultFilter?: any;
};

type TApiResponse = {
  data?: {
    REPORT_DATA: any;
    COUNT?: number;
  };
};

export const ReportWrapper: React.FC<TReportWrapper> = ({
  reportID,
  reportName,
  metaData,
  getAPIFn,
  otherAPIRequestPara,
  maxHeight,
  onClickActionEvent,
  defaultFilter,
}) => {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [error, setError] = useState(null);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const fetchOptions = useRef<any>(null);
  const abortAPICallRef = useRef<any>(null);

  const mutation: any = useMutation<TApiResponse, any>(getAPIFn, {
    onError: (error: any) => {
      setError(error);
    },
    onSuccess: (result) => {
      setData(result?.[0].REPORT_DATA);
      setPageCount(
        Math.ceil(
          Number(result?.[0].COUNT ?? 1) /
            Number(fetchOptions.current?.pageSize ?? 20),
        ),
      );
      setTotalRecords(Number(result?.[0].COUNT ?? 1));
    },
  });

  const fetchData = useCallback(
    ({ pageSize, pageIndex, filters, sortBy }) => {
      const startRow = Number(pageSize) * Number(pageIndex) + 1;
      const endRow = Number(startRow) + Number(pageSize) - 1;
      let childFilter = formatFilterBy(filters);
      let childSortBy = formatSortBy(sortBy);

      if (abortAPICallRef.current) {
        abortAPICallRef.current?.abort?.();
      }
      abortAPICallRef.current = new AbortController();
      const requestPara = {
        reportID,
        APIRequestPara: {
          FROM_ROW: startRow,
          TO_ROW: endRow,
          FILTER_CONDITIONS: childFilter,
          ORDERBY_COLUMNS: childSortBy,
          IS_PAGE: true,
          ...otherAPIRequestPara,
        },
        abortAPICall: abortAPICallRef.current,
      };
      fetchOptions.current = { pageSize };
      mutation.mutate(requestPara);
    },
    [setTotalRecords, setData],
  );

  // added to hide selected columns from metadata
  const hiddenColumns = extractHiddenColumns(metaData.columns);
  const transformedMetadataWithHiddenColumns = useMemo(() => {
    return { ...metaData, hiddenColumns };
  }, [hiddenColumns]);

  return (
    <ReportGrid
      metaData={transformedMetadataWithHiddenColumns}
      maxHeight={maxHeight}
      reportName={reportName}
      title={t(metaData.title)}
      hideFooter={metaData.hideFooter}
      hideAmountIn={metaData.hideAmountIn}
      // removing retrievalType for server side pagination
      // retrievalType={metaData.retrievalType}
      onClickActionEvent={onClickActionEvent}
      // removing option for groupBy for server side pagination
      // initialState={{
      //   groupBy: metaData?.groupBy ?? [],
      // }}
      // options={{
      //   disableGroupBy: metaData.disableGroupBy,
      // }}
      // autoFetch={metaData?.autoFetch ?? true}
      autoFetch={true} // setting autoFetch true since we are not using retrieval box for server side pagination
      data={data}
      loading={mutation.isLoading}
      isError={mutation.isError}
      error={error}
      pageCount={pageCount}
      totalRecords={totalRecords}
      pageSizes={
        metaData?.pageSize ?? [
          25, 50, 100, 250, 500, 2500, 5000, 10000, 50000, 100000,
        ]
      }
      // pageSizes={metaData?.pageSize ?? [15, 25, 50]}
      defaultPageSize={metaData.defaultPageSize ?? 25}
      onFetchData={fetchData}
      defaultFilter={defaultFilter}
    />
  );
};

// export default ReportWrapper;
